/**
 * English UI text, the source of truth for every language. Only the site's own labels live
 * here: data from the API (team, market, game and provider names, amounts) stays as sent.
 * `{name}` placeholders are filled by `t(key, { name })`.
 */
export const en = {
  // Header navigation
  'nav.inPlay': 'In-Play',
  'nav.aviator': 'Aviator',

  // Bottom navigation bar
  'bottomNav.home': 'Home',
  'bottomNav.inPlay': 'In Play',
  'bottomNav.offers': 'Offers',
  'bottomNav.myMarkets': 'My Markets',
  'bottomNav.referral': 'Referral',
  'bottomNav.passbook': 'Passbook',
  'bottomNav.profile': 'Profile',

  // Shared actions and labels
  'common.deposit': 'Deposit',
  'common.withdraw': 'Withdraw',
  'common.rules': 'Rules',
  'common.all': 'All',
  'common.viewAll': 'View all',
  'common.loading': 'Loading...',
  'auth.login': 'Login',
  'auth.signup': 'Signup',

  // Language menu
  'language.select': 'Select Language',

  // Search
  'search.placeholder': 'Search matches / casino games',
  'search.short': 'Search',
  'search.matches': 'Matches',
  'search.casinoGames': 'Casino games',
  'search.searching': 'Searching…',
  'search.noResults': 'No results for “{term}”',
  'search.hint': 'Try a team, league or game name.',

  // User menu
  'menu.accountStatement': 'Account Statement',
  'menu.betHistory': 'Bet History',
  'menu.activityLogs': 'Activity Logs',
  'menu.casinoBetHistory': 'Casino Bet History',
  'menu.bonusStatement': 'Bonus Statement',
  'menu.referral': 'Referral',
  'menu.setButtonValues': 'Set Button Values',
  'menu.changePassword': 'Change Password',
  'menu.balance': 'Balance',
  'menu.exposure': 'Exposure',
  'menu.signOut': 'Sign Out',

  // Sidebar
  'sidebar.sports': 'Sports',
  'sidebar.casino': 'Casino',
  'sidebar.soon': 'Soon',
  'sidebar.noCompetitions': 'No competitions',
  'sidebar.noMatches': 'No matches',

  // Home page
  'home.casinoProviders': 'Casino Providers',
  'home.live': 'Live',
  'home.virtual': 'Virtual',
  'home.noLiveMatches': 'No live matches right now',
  'home.noMatches': 'No matches available',

  // Sport names (keys follow the labels in src/config/sports.ts)
  'sport.Cricket': 'Cricket',
  'sport.Football': 'Football',
  'sport.Tennis': 'Tennis',
  'sport.Kabaddi': 'Kabaddi',
  'sport.Politics': 'Politics',
  'sport.Basketball': 'Basketball',
  'sport.Baseball': 'Baseball',
  'sport.Table Tennis': 'Table Tennis',
  'sport.Volleyball': 'Volleyball',
  'sport.Ice Hockey': 'Ice Hockey',
  'sport.Rugby': 'Rugby',
  'sport.Mixed Martial Arts': 'Mixed Martial Arts',
  'sport.Darts': 'Darts',
  'sport.Futsal': 'Futsal',
  'sport.Horse Racing': 'Horse Racing',
  'sport.Greyhound Racing': 'Greyhound Racing',

  // Match details page
  'market.limits': 'Min: {min} | Max: {max}',
  'market.cashout': 'Cashout',

  // Profile page
  'menu.profile': 'Profile',
  'menu.transactionReport': 'Transaction Report',
  'profile.activeBets': 'Active Bets',
  'profile.joinUs': 'Join us now',
  'profile.logout': 'Logout',
  'profile.balance': 'Balance',

  // Profile page
  'profile.description': 'Your balance, wallet, saved accounts and reports in one place.',
  'profile.exposure': 'Exposure',
  'profile.bonus': 'Bonus',

  // Deposit popup
  'deposit.availableBalance': 'Available Balance',
  'deposit.coins': 'Deposit Coins',
  'deposit.enterCoins': 'Enter Coins',
  'deposit.makePayment': 'Make Payments',
  'deposit.loadingMethods': 'Loading payment methods...',
  'deposit.methodsError': 'Payment methods could not be loaded. Please try again.',
  'deposit.noMethods': 'No payment methods are available right now.',
  'deposit.uploadScreenshot': 'Click Here To Upload Payment Screenshot',
  'deposit.optional': '(Optional)',
  'deposit.utrLabel': 'Unique Transaction Reference',
  'deposit.utrPlaceholder': '6 to 23 Digit UTR/RRN Number',
  'deposit.txHash': 'Transaction Hash',
  'deposit.paste': 'Paste',
  'deposit.copy': 'Copy',
  'deposit.submit': 'Submit',
  'deposit.submitting': 'Submitting...',
  'deposit.scanning': 'Reading UTR from screenshot...',
  'deposit.scanFound': 'UTR filled from your screenshot, please check it matches.',
  'deposit.scanNotFound': "Couldn't read the UTR from the screenshot, please enter it.",
  'deposit.upiId': 'UPI ID',
  'deposit.name': 'Name',
  'deposit.walletAddress': 'Wallet Address',
  'deposit.send': 'Send',
  'deposit.rateUnavailable': 'Conversion rate is unavailable, please try again later.',
  'deposit.range': 'Deposit between {min} and {max} coins',
  'deposit.rangeFrom': 'Minimum deposit {min} coins',
  'deposit.noMethodForAmount': 'No payment method accepts this amount. Please change it.',
  'deposit.displayName': 'Display Name',
  'deposit.upiDetails': 'Upi Details',
  'deposit.bankDetails': 'Bank Details',
  'deposit.cryptoDetails': 'Crypto Details',
  'deposit.bankName': 'Bank Name',
  'deposit.accountHolderName': 'Account Holder Name',
  'deposit.accountNumber': 'Account Number',
  'deposit.ifsc': 'IFSC Code',
  'deposit.amount': 'Amount',
  'deposit.hashCode': 'Hash Code',
  'deposit.enterHash': 'Enter Hash Code',

  // Withdraw popup
  'withdraw.coins': 'Withdraw Coins',
  'withdraw.minimum': 'Minimum Withdraw Amount {min} Coins',
  'withdraw.overBalance': 'Amount is more than your available balance',
  'withdraw.continue': 'Continue',
  'withdraw.loadingAccounts': 'Loading your accounts...',
  'withdraw.accountsError': 'Your accounts could not be loaded. Please try again.',
  'withdraw.noAccounts': 'No saved accounts yet. Add a bank, UPI or crypto account to withdraw.',
  'withdraw.addAccount': 'Add Account',
  'withdraw.bankAccount': 'Bank Account',
  'withdraw.cryptoWallet': 'Crypto Wallet',
  'withdraw.accountHolder': 'Account Holder',
  'withdraw.accountNumber': 'Account Number',
  'withdraw.ifsc': 'IFSC',
  'withdraw.amount': 'Amount',
  'withdraw.youReceive': 'You receive',
  'withdraw.submit': 'Withdraw {amount}',
  'withdraw.processing': 'Processing...',
} as const;

export type TranslationKey = keyof typeof en;
export type Translations = Record<TranslationKey, string>;
